import React, { createContext, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { getUserById } from "../service/userService";

export const UserContext = createContext();

export const UserProvider = ({ children }) => {
    const [user, setUser] = useState({
        firstName: "",
        lastName: "",
        profile_pic: "",
        role: "",
        permissions: [],
        isAuthenticated: false
    });
    const [loading, setLoading] = useState(true); // Add loading state

    useEffect(() => {
        const fetchUserData = async () => {
            try {
                setLoading(true); // Set loading to true at the start
                const storedUserData = localStorage.getItem("user");
                if (!storedUserData) {
                    setLoading(false); // No user data, stop loading
                    return;
                }

                const userData = JSON.parse(storedUserData);
                const userObj = userData?.data;
                if (!userObj?._id) {
                    setLoading(false); // Invalid user ID, stop loading
                    return;
                }

                const response = await getUserById(userObj._id);
                const { data } = response;

                const [firstName, ...lastNameParts] = data.name.split(' ');
                const lastName = lastNameParts.join(' ');

                const permissions = data.role?.modules?.map(module => {
                    let moduleLabel = "Unknown Module";
                    if (module.moduleId) {
                        moduleLabel = typeof module.moduleId === 'object' 
                            ? module.moduleId.label 
                            : "Module ID: " + module.moduleId;
                    }

                    return {
                        moduleId: module.moduleId?._id || module.moduleId,
                        label: moduleLabel,
                        functions: module.functions?.map(func => {
                            let functionLabel = "Unknown Function";
                            if (func.functionId) {
                                functionLabel = typeof func.functionId === 'object' 
                                    ? func.functionId.label 
                                    : "Function ID: " + func.functionId;
                            }

                            return {
                                functionId: func.functionId?._id || func.functionId,
                                label: functionLabel,
                                permissions: func.permissions || {},
                                subFunctions: func.subFunctions?.map(subFunc => {
                                    let subFunctionLabel = "Unknown Subfunction";
                                    if (subFunc.functionId) {
                                        subFunctionLabel = typeof subFunc.functionId === 'object' 
                                            ? subFunc.functionId.label 
                                            : "Subfunction ID: " + subFunc.functionId;
                                    }

                                    return {
                                        functionId: subFunc.functionId?._id || subFunc.functionId,
                                        label: subFunctionLabel,
                                        permissions: subFunc.permissions || {}
                                    };
                                }) || []
                            };
                        }) || []
                    };
                }) || [];

                setUser({
                    firstName,
                    lastName,
                    profile_pic: data.profile_picture,
                    role: data.role?.roleName,
                    permissions,
                    isAuthenticated: true
                });
            } catch (err) {
                console.error("Failed to fetch user data:", err);
            } finally {
                setLoading(false); // Stop loading regardless of success or failure
            }
        };

        fetchUserData();
    }, []);

    return (
        <UserContext.Provider value={{ user, setUser, loading }}>
            {children}
        </UserContext.Provider>
    );
};

UserProvider.propTypes = {
    children: PropTypes.node.isRequired,
};